import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApplicationEnum } from "~/@types/enums";
import localizedText, { type TranslateKey, translateWithActiveLanguage } from "~/helpers/localizedText";
import moduleBrandingConfig from "~/layout/moduleBrandingConfig";

export type ModuleBranding = {
	displayName: string;
	logoSrc: string;
};

export const DEFAULT_MODULE_LOGO_SRC = "/logos/company.svg";

const MODULE_LABEL_KEYS: Record<ApplicationEnum, string> = {
	[ApplicationEnum.ADMIN]: "nav.system.administration.label",
	[ApplicationEnum.AVAILABILITY]: "nav.availability.module.label",
	[ApplicationEnum.BENCHMARK]: "nav.system.benchmark.label",
	[ApplicationEnum.COUNT]: "nav.count.module.label",
	[ApplicationEnum.ENGAGE]: "nav.engage.newCount.label",
	[ApplicationEnum.EVENTS]: "nav.events.module.label",
	[ApplicationEnum.FORECAST]: "nav.forecast.module.label",
	[ApplicationEnum.FULFILL]: "home.sections.workSuite",
	[ApplicationEnum.INTELLIGENCE]: "nav.system.intelligence.label",
	[ApplicationEnum.MATERIAL]: "home.sections.workSuite",
	[ApplicationEnum.PORTAL]: "home.sections.workSuite",
	[ApplicationEnum.PRODUCE]: "home.sections.workSuite",
	[ApplicationEnum.REPORTS]: "nav.system.reports.label",
	[ApplicationEnum.RESTOCK]: "home.sections.workSuite",
	[ApplicationEnum.SETTINGS]: "nav.system.settings.label",
	[ApplicationEnum.VERIFY]: "home.sections.workSuite",
	[ApplicationEnum.WORKSUITE]: "home.sections.workSuite"
};

const MODULE_FALLBACK_NAMES: Record<ApplicationEnum, string> = {
	[ApplicationEnum.ADMIN]: "Administration",
	[ApplicationEnum.AVAILABILITY]: "Availability",
	[ApplicationEnum.BENCHMARK]: "Benchmark",
	[ApplicationEnum.COUNT]: "Count",
	[ApplicationEnum.ENGAGE]: "Engage",
	[ApplicationEnum.EVENTS]: "Events",
	[ApplicationEnum.FORECAST]: "Forecast",
	[ApplicationEnum.FULFILL]: "Fulfill",
	[ApplicationEnum.INTELLIGENCE]: "Intelligence",
	[ApplicationEnum.MATERIAL]: "Material",
	[ApplicationEnum.PORTAL]: "Portal",
	[ApplicationEnum.PRODUCE]: "Produce",
	[ApplicationEnum.REPORTS]: "Reports",
	[ApplicationEnum.RESTOCK]: "Restock",
	[ApplicationEnum.SETTINGS]: "Settings",
	[ApplicationEnum.VERIFY]: "Verify",
	[ApplicationEnum.WORKSUITE]: "Work Suite"
};

const DIRECT_TRANSLATED_MODULES = new Set<ApplicationEnum>([
	ApplicationEnum.ADMIN,
	ApplicationEnum.AVAILABILITY,
	ApplicationEnum.BENCHMARK,
	ApplicationEnum.COUNT,
	ApplicationEnum.EVENTS,
	ApplicationEnum.FORECAST,
	ApplicationEnum.INTELLIGENCE,
	ApplicationEnum.REPORTS,
	ApplicationEnum.SETTINGS,
	ApplicationEnum.WORKSUITE
]);

const APPLICATION_BY_SEGMENT = new Map<string, ApplicationEnum>(
	Object.values(ApplicationEnum).map((module) => [module, module])
);

const resolveRootModule = (path: string): ApplicationEnum | undefined => {
	const segments = path.split("/").filter(Boolean);
	if (segments.length !== 1) return undefined;
	return APPLICATION_BY_SEGMENT.get(segments[0]);
};

export function resolveModuleBranding(
	module: ApplicationEnum,
	config: Partial<Record<ApplicationEnum, Partial<ModuleBranding>>>,
	defaults: ModuleBranding
): ModuleBranding {
	const override = config[module];
	const displayName = override?.displayName?.trim() || defaults.displayName;
	const logoSrc = override?.logoSrc?.trim() || defaults.logoSrc;
	return { displayName, logoSrc };
}

export function resolveLocationMapLabel(
	path: string,
	fallbackText: string,
	key?: string,
	translate: TranslateKey = translateWithActiveLanguage,
	config: Partial<Record<ApplicationEnum, Partial<ModuleBranding>>> = moduleBrandingConfig
): string {
	const defaultLabel = localizedText(fallbackText, key, translate);
	const rootModule = resolveRootModule(path);
	if (!rootModule) return defaultLabel;
	return resolveModuleBranding(rootModule, config, {
		displayName: defaultLabel,
		logoSrc: DEFAULT_MODULE_LOGO_SRC
	}).displayName;
}

export function useModuleBranding(module: ApplicationEnum): ModuleBranding {
	const { t } = useTranslation("common");
	const defaultDisplayName = DIRECT_TRANSLATED_MODULES.has(module)
		? t(MODULE_LABEL_KEYS[module])
		: MODULE_FALLBACK_NAMES[module];
	const overrideDisplayName = moduleBrandingConfig[module]?.displayName;
	const overrideLogoSrc = moduleBrandingConfig[module]?.logoSrc;

	return useMemo(
		() =>
			resolveModuleBranding(module, moduleBrandingConfig, {
				displayName: defaultDisplayName,
				logoSrc: DEFAULT_MODULE_LOGO_SRC
			}),
		[defaultDisplayName, module, overrideDisplayName, overrideLogoSrc]
	);
}

export default useModuleBranding;
